import React, { useState } from 'react';
import { Settings as SettingsIcon, Moon, Sun, Volume2, VolumeX, Globe, Shield, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import UserManagement from './UserManagement';

/**
 * Settings Component
 * Preferences, application settings, and user management
 *
 * Features:
 * - Preferences (theme, sound, language)
 * - Accessibility options
 * - Access control management (users, roles, sections)
 */
const Settings = () => {
  const { t, i18n } = useTranslation();
  const [selectedSection, setSelectedSection] = useState(null);
  const [settings, setSettings] = useState({
    darkMode: localStorage.getItem('darkMode') === 'true',
    soundEnabled: localStorage.getItem('soundEnabled') !== 'false',
    language: i18n.language || 'it',
    notifications: localStorage.getItem('notifications') !== 'false',
    highContrast: localStorage.getItem('highContrast') === 'true',
    fontSize: localStorage.getItem('fontSize') || 'normal'
  });

  // Configuration sections available
  const configSections = [
    {
      id: 'preferences',
      name: t('settings.preferences'),
      description: t('customizePreferencesAndManageAccessControl'),
      icon: SettingsIcon,
      color: 'bg-primary'
    },
    {
      id: 'access-control',
      name: t('userManagementAndAccessControl'),
      description: t('userManagementAndAccessControl'),
      icon: Shield,
      color: 'bg-danger'
    }
  ];

  // If a section is selected, render that section
  if (selectedSection === 'access-control') {
    return <UserManagement onBack={() => setSelectedSection(null)} />;
  }

  // Handle dark mode toggle
  const toggleDarkMode = () => {
    const newValue = !settings.darkMode;
    setSettings({ ...settings, darkMode: newValue });
    localStorage.setItem('darkMode', newValue);
    if (newValue) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  };

  // Handle sound toggle
  const toggleSound = () => {
    const newValue = !settings.soundEnabled;
    setSettings({ ...settings, soundEnabled: newValue });
    localStorage.setItem('soundEnabled', newValue);
  };

  // Handle language change
  const handleLanguageChange = (lang) => {
    setSettings({ ...settings, language: lang });
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  // Handle notifications toggle
  const toggleNotifications = () => {
    const newValue = !settings.notifications;
    setSettings({ ...settings, notifications: newValue });
    localStorage.setItem('notifications', newValue);
  };

  // Handle high contrast toggle
  const toggleHighContrast = () => {
    const newValue = !settings.highContrast;
    setSettings({ ...settings, highContrast: newValue });
    localStorage.setItem('highContrast', newValue);
    if (newValue) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
  };

  // Handle font size change
  const handleFontSizeChange = (size) => {
    setSettings({ ...settings, fontSize: size });
    localStorage.setItem('fontSize', size);
    document.body.setAttribute('data-font-size', size);
  };

  // Show Preferences section
  if (selectedSection === 'preferences') {
    return (
      <div className="settings-container">
        {/* Settings Header with Back Button */}
        <header className="settings-header">
          <div className="d-flex align-items-center gap-3 mb-0" style={{ flexWrap: 'wrap' }}>
            {selectedSection === 'preferences' && (
              <button
                className="btn-back-header"
                onClick={() => setSelectedSection(null)}
                aria-label="Back to settings menu"
                title="Back to settings menu"
              >
                ← Back
              </button>
            )}
            <div>
              <h1 className="mb-0">{t('settings.preferences')}</h1>
              <p className="mb-0 text-white-50">{t('customizePreferencesAndManageAccessControl')}</p>
            </div>
          </div>
        </header>

        {/* Preferences Content */}
        <div className="settings-content">
        <div className="row g-4">
          {/* Appearance Settings */}
          <div className="col-12 col-lg-6">
            <section className="settings-section">
              <h2 className="settings-section-title">
                <Moon size={24} className="me-2" aria-hidden="true" />
                {t('settings.appearance')}
              </h2>

              {/* Dark Mode */}
              <div className="settings-item">
                <div className="settings-label">
                  <label htmlFor="dark-mode-toggle" className="form-label mb-0">
                    {t('settings.darkMode')}
                  </label>
                  <p className="settings-description">{t('settings.darkModeDescription')}</p>
                </div>
                <div className="settings-control">
                  <button
                    id="dark-mode-toggle"
                    className={`btn ${settings.darkMode ? 'btn-dark' : 'btn-light'}`}
                    onClick={toggleDarkMode}
                    aria-label={`Dark mode is ${settings.darkMode ? 'enabled' : 'disabled'}`}
                    aria-pressed={settings.darkMode}
                  >
                    {settings.darkMode ? <Moon size={20} /> : <Sun size={20} />}
                  </button>
                </div>
              </div>

              {/* High Contrast */}
              <div className="settings-item">
                <div className="settings-label">
                  <label htmlFor="high-contrast-toggle" className="form-label mb-0">
                    {t('settings.highContrast')}
                  </label>
                  <p className="settings-description">{t('settings.highContrastDescription')}</p>
                </div>
                <div className="settings-control">
                  <button
                    id="high-contrast-toggle"
                    className={`btn ${settings.highContrast ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={toggleHighContrast}
                    aria-label={`High contrast is ${settings.highContrast ? 'enabled' : 'disabled'}`}
                    aria-pressed={settings.highContrast}
                  >
                    {settings.highContrast ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>

              {/* Font Size */}
              <div className="settings-item">
                <div className="settings-label">
                  <label htmlFor="font-size-select" className="form-label mb-0">
                    {t('settings.fontSize')}
                  </label>
                  <p className="settings-description">{t('settings.fontSizeDescription')}</p>
                </div>
                <div className="settings-control">
                  <select
                    id="font-size-select"
                    className="form-select"
                    value={settings.fontSize}
                    onChange={(e) => handleFontSizeChange(e.target.value)}
                    aria-label="Select font size"
                  >
                    <option value="small">{t('settings.fontSizeSmall')}</option>
                    <option value="normal">{t('settings.fontSizeNormal')}</option>
                    <option value="large">{t('settings.fontSizeLarge')}</option>
                    <option value="extra-large">{t('settings.fontSizeExtraLarge')}</option>
                  </select>
                </div>
              </div>
            </section>
          </div>

          {/* Sound & Notifications */}
          <div className="col-12 col-lg-6">
            <section className="settings-section">
              <h2 className="settings-section-title">
                <Volume2 size={24} className="me-2" aria-hidden="true" />
                {t('settings.soundAndNotifications')}
              </h2>

              {/* Sound Toggle */}
              <div className="settings-item">
                <div className="settings-label">
                  <label htmlFor="sound-toggle" className="form-label mb-0">
                    {t('settings.soundEffects')}
                  </label>
                  <p className="settings-description">{t('settings.soundEffectsDescription')}</p>
                </div>
                <div className="settings-control">
                  <button
                    id="sound-toggle"
                    className={`btn ${settings.soundEnabled ? 'btn-success' : 'btn-secondary'}`}
                    onClick={toggleSound}
                    aria-label={`Sound is ${settings.soundEnabled ? 'enabled' : 'disabled'}`}
                    aria-pressed={settings.soundEnabled}
                  >
                    {settings.soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                  </button>
                </div>
              </div>

              {/* Notifications Toggle */}
              <div className="settings-item">
                <div className="settings-label">
                  <label htmlFor="notifications-toggle" className="form-label mb-0">
                    {t('settings.notificationLabel')}
                  </label>
                  <p className="settings-description">{t('settings.notificationDescription')}</p>
                </div>
                <div className="settings-control">
                  <button
                    id="notifications-toggle"
                    className={`btn ${settings.notifications ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={toggleNotifications}
                    aria-label={`Notifications are ${settings.notifications ? 'enabled' : 'disabled'}`}
                    aria-pressed={settings.notifications}
                  >
                    {settings.notifications ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </section>
          </div>

          {/* Language Settings */}
          <div className="col-12 col-lg-6">
            <section className="settings-section">
              <h2 className="settings-section-title">
                <Globe size={24} className="me-2" aria-hidden="true" />
                {t('settings.selectLanguage')}
              </h2>

              {/* Language Selection */}
              <div className="settings-item">
                <div className="settings-label">
                  <label htmlFor="language-select" className="form-label mb-0">
                    {t('settings.selectLanguage')}
                  </label>
                  <p className="settings-description">{t('settings.chooseLanguage')}</p>
                </div>
                <div className="settings-control">
                  <div className="btn-group" role="group" aria-label="Language selection">
                    <button
                      type="button"
                      className={`btn ${settings.language === 'it' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleLanguageChange('it')}
                      aria-pressed={settings.language === 'it'}
                    >
                      {t('settings.italian')}
                    </button>
                    <button
                      type="button"
                      className={`btn ${settings.language === 'en' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleLanguageChange('en')}
                      aria-pressed={settings.language === 'en'}
                    >
                      {t('settings.english')}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* About Section */}
          <div className="col-12 col-lg-6">
            <section className="settings-section">
              <h2 className="settings-section-title">{t('settings.about')}</h2>

              <div className="about-info">
                <div className="info-item mb-3">
                  <strong>{t('settings.application')}</strong>
                  <p className="mb-0">{t('settings.appDescription')}</p>
                </div>
                <div className="info-item mb-3">
                  <strong>{t('settings.version')}</strong>
                  <p className="mb-0">{t('settings.versionNumber')}</p>
                </div>
                <div className="info-item mb-3">
                  <strong>{t('settings.lastUpdated')}</strong>
                  <p className="mb-0">{t('settings.lastUpdatedDate')}</p>
                </div>
                <div className="info-item">
                  <strong>{t('settings.dataPrivacy')}</strong>
                  <p className="mb-0">{t('settings.dataPrivacyDesc')}</p>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Reset to Defaults Button */}
        <div className="settings-actions mt-4">
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              if (window.confirm(t('areYouSureYouWantToReset'))) {
                localStorage.clear();
                setSettings({
                  darkMode: false,
                  soundEnabled: true,
                  language: 'it',
                  notifications: true,
                highContrast: false,
                fontSize: 'normal'
              });
              document.body.classList.remove('dark-mode', 'high-contrast');
              window.location.reload();
              }
            }}
            aria-label="Reset all settings to default values"
          >
            {t('settings.resetToDefaults')}
          </button>
        </div>
        </div>
      </div>
    );
  }

  // Show Settings Menu (default view)
  return (
    <div className="settings-container">
      {/* Settings Header */}
      <header className="settings-header">
        <div className="d-flex align-items-center gap-3 mb-0">
          <SettingsIcon size={40} className="text-white" aria-hidden="true" />
          <div>
            <h1 className="mb-0">{t('settings.settingsAndConfiguration')}</h1>
            <p className="mb-0 text-white-50">{t('settings.customizeYourExperience')}</p>
          </div>
        </div>
      </header>

      {/* Configuration Cards Grid */}
      <section className="settings-cards-grid" aria-label="Configuration sections">
        {configSections.map((section) => {
          const IconComponent = section.icon;
          return (
            <article
              key={section.id}
              className="settings-card"
              onClick={() => setSelectedSection(section.id)}
              role="button"
              tabIndex={0}
              aria-label={`Navigate to ${section.name}: ${section.description}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedSection(section.id);
                }
              }}
            >
              <div className={`settings-card-icon ${section.color}`}>
                <IconComponent size={32} aria-hidden="true" />
              </div>
              <h2 className="settings-card-title">{section.name}</h2>
              <p className="settings-card-description">{section.description}</p>
              <div className="settings-card-footer">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSection(section.id);
                  }}
                  aria-label={`Open ${section.name} settings`}
                >
                  Open <ChevronRight size={16} />
                </button>
              </div>
            </article>
          );
        })}
      </section>

      {/* Info Alert */}
      <div className="alert alert-info mt-4" role="alert">
        <strong>ℹ️ {t('note')}:</strong> {t('settings.settingsInfo')}
      </div>
    </div>
  );
};

export default Settings;
